import React, { useState } from 'react';
import { makeStyles, Button, Grid } from '@material-ui/core';
import { Typography } from '@swagup-com/components';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import Modal from './modal';
import step1Image from '../../assets/images/intro/Container-step-1.svg';
import step2Image from '../../assets/images/intro/Container-step-2.svg';
import step3Image from '../../assets/images/intro/Container-step-3.svg';
import ProgressBar from './progressBar';
import { CommonButton } from './commonButtons';
import { RightArrowIcon, BackArrowIcon } from '../icons';
import styles from './packBuilderIntroModal.styles';
import { shoppingExperienceColors } from '../constants';

const useStyles = makeStyles(styles);

const steps = [
  {
    image: step1Image,
    title: 'You are building a Swag Pack',
    description: 'Perfect for onboarding, employee appreciation, customer outreach and more.',
    subtitle: 'First create a name for your pack',
    inputPlaceholder: 'Pack name here',
    inputHint: 'For example: "Welcome Pack" or “Customer Appreciation Pack”',
    buttonText: 'Continue'
  },
  {
    image: step2Image,
    title: 'Select items to add to Swag Pack',
    description:
      'Explore our amazing selection of products. Tap "Add more items" to browse our complete product collection.',
    buttonText: 'Continue'
  },
  {
    image: step3Image,
    title: 'Customize your Swag Pack',
    description:
      'Add packaging, choose quantity, and select more items. Tap "View cart" to review and check out your project.',
    buttonText: 'Ready to start'
  }
];

const PackBuilderIntroModal = ({ open, onClose }) => {
  const classes = useStyles();
  const [currentStep, setCurrentStep] = useState(0);
  const [packName, setPackName] = useState('');

  const handleNext = () => {
    if (currentStep === steps.length - 1) {
      localStorage.setItem('hasSeenPackBuilderIntro', 'true');
      onClose();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleClose = () => {
    localStorage.setItem('hasSeenPackBuilderIntro', 'true');
    onClose();
  };

  const step = steps[currentStep];

  const validatePackName = name => {
    const trimmed = name.trim();

    // Example rule: required
    if (trimmed === '') return false;

    // Example rule: minimum length 3
    if (trimmed.length < 3) return false;

    // Example rule: only letters, numbers, spaces (no symbols)
    const allowedPattern = /^[a-zA-Z0-9 ]+$/;
    if (!allowedPattern.test(trimmed)) return false;

    return true;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      classes={{
        content: classes.content
      }}
      disableBackdropClick
      innerClasses={{ paper: classes.dialogModal }}
    >
      <div className={classes.container}>
        <Grid container className={classes.gridWrapper}>
          <Grid item xs={12} md={6} className={classes.leftSection}>
            <img src={step.image} alt={`Step ${currentStep + 1}`} className={classes.stepImage} />
          </Grid>

          <Grid item xs={12} md={6} className={classes.rightSection}>
            <div className={classes.rightSectionWrapper}>
              <div className={classes.stepNumberCircle}>
                <span className={classes.stepNumber}>{currentStep + 1}</span>
              </div>
              <div className={classes.modalContentWrapper}>
                <div className={classes.modalContentWrapper}>
                  <Typography variant="h3SemiBoldInter">{step.title}</Typography>
                  <Typography variant="body2RegularInter">{step.description}</Typography>
                </div>

                {currentStep === 0 && (
                  <div className={classes.modalContentWrapper}>
                    <Typography variant="body1SemiBoldInter">{step.subtitle}</Typography>
                    <div>
                      <input
                        type="text"
                        placeholder={step.inputPlaceholder}
                        value={packName}
                        onChange={e => setPackName(e.target.value)}
                        className={classes.input}
                      />
                      <Typography variant="body3RegularInter">{step.inputHint}</Typography>
                    </div>
                  </div>
                )}

                <div className={classes.buttonGroup}>
                  {currentStep > 0 && (
                    <CommonButton
                      id="Go back"
                      data-gtm-id="Go back"
                      text="Go back"
                      outlined
                      mt={0}
                      style={{
                        marginTop: 0,
                        width: '134px'
                      }}
                      hasIcon
                      startIcon
                      icon={<BackArrowIcon color={shoppingExperienceColors.black} />}
                      onClick={handleBack}
                    />
                  )}
                  <CommonButton
                    id={step.buttonText}
                    data-gtm-id={step.buttonText}
                    text={step.buttonText}
                    isActive
                    mt={0}
                    style={{
                      marginTop: 0,
                      width: currentStep < 2 ? '134px' : '172px'
                    }}
                    hasIcon
                    icon={<RightArrowIcon color={shoppingExperienceColors.white} />}
                    onClick={handleNext}
                    disabled={currentStep === 0 && !validatePackName(packName)}
                  />
                </div>
              </div>
              <ProgressBar currentStep={currentStep + 1} totalSteps={3} containerClassName={classes.progressBar} />
            </div>

            <CommonButton
              id={step.buttonText}
              data-gtm-id={step.buttonText}
              text="I’m ready to customize my Swag Pack"
              isActive
              mt={0}
              style={{
                height: '48px',
                borderRadius: 4,
                gap: 10,
                color: '#2663FF',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 16,
                lineHeight: '16px',
                letterSpacing: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textTransform: 'none',
                textDecoration: 'none',
                backgroundColor: 'transparent',
                border: 'none',
                boxShadow: 'none',
                marginTop: 16,
                hoverColor: 'rgba(45, 98, 237, 0.1)'
              }}
              hasIcon
              icon={<RightArrowIcon color={shoppingExperienceColors.primary} />}
              // TODO : Update logic later
              onClick={handleClose}
            />
          </Grid>
        </Grid>
      </div>
    </Modal>
  );
};

export default PackBuilderIntroModal;
